import { FormEvent, useEffect, useState } from "react";
import { navigate } from "../utils/navigation";
import { LanguageSwitcher } from "../i18n";

type Session = { authenticated: boolean; username: string | null };

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    ...init,
    headers: { ...(init?.body ? { "content-type": "application/json" } : {}), ...init?.headers },
  });
  const data = await response.json() as T & { error?: string };
  if (!response.ok) throw new Error(data.error || "请求失败");
  return data;
}

export function AccountPage() {
  const [mode, setMode] = useState<"login" | "register">("login");
  const [username, setUsername] = useState("");
  const [showUsername, setShowUsername] = useState(true);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [session, setSession] = useState<Session>({ authenticated: false, username: null });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    void api<Session>("/api/auth/session").then(setSession).catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError("");
    if (!/^[A-Za-z0-9]{6,128}$/.test(password)) {
      setError("密码需为 6–128 位英文字母或数字");
      return;
    }
    if (mode === "register" && password !== confirmPassword) {
      setError("两次输入的密码不一致");
      return;
    }
    setSubmitting(true);
    try {
      const result = await api<{ username: string }>(`/api/auth/${mode}`, {
        method: "POST",
        body: JSON.stringify({ username, password }),
      });
      setSession({ authenticated: true, username: result.username });
      window.dispatchEvent(new Event("xm:auth-changed"));
      setPassword("");
      setConfirmPassword("");
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : "操作失败");
    } finally {
      setSubmitting(false);
    }
  };

  const logout = async () => {
    await api("/api/auth/logout", { method: "POST", body: "{}" });
    setSession({ authenticated: false, username: null });
    window.dispatchEvent(new Event("xm:auth-changed"));
  };

  return <main className="account-page">
    <button className="back-link" onClick={() => navigate("/")}>← 返回工具箱</button>
    <div className="standalone-language-switcher"><LanguageSwitcher /></div>
    <section className="account-card">
      <div className="brand-mark">明</div>
      {loading ? <div className="loader dark-loader" /> : session.authenticated ? <>
        <h1>你好，{session.username}</h1>
        <p className="account-copy">你已登录。</p>
        <div className="account-actions"><button className="primary-button" onClick={() => navigate("/")}>进入工具箱</button><button className="secondary-button" onClick={() => void logout()}>退出登录</button></div>
      </> : <>
        <h1>{mode === "login" ? "登录账号" : "创建账号"}</h1>
        <div className="account-tabs"><button className={mode === "login" ? "active" : ""} onClick={() => { setMode("login"); setError(""); }}>登录</button><button className={mode === "register" ? "active" : ""} onClick={() => { setMode("register"); setError(""); }}>注册</button></div>
        <form onSubmit={submit}>
          <label className="field"><span>用户名</span><div className="account-input-wrap"><input required minLength={3} maxLength={24} type={showUsername ? "text" : "password"} autoComplete="username" value={username} onChange={(event) => setUsername(event.target.value)} /><VisibilityToggle visible={showUsername} onToggle={() => setShowUsername((visible) => !visible)} field="用户名" /></div></label>
          <label className="field"><span>密码</span><div className="account-input-wrap"><input required minLength={6} maxLength={128} type={showPassword ? "text" : "password"} autoComplete={mode === "login" ? "current-password" : "new-password"} value={password} onChange={(event) => setPassword(event.target.value)} /><VisibilityToggle visible={showPassword} onToggle={() => setShowPassword((visible) => !visible)} field="密码" /></div></label>
          {mode === "register" && <label className="field"><span>重复密码</span><div className="account-input-wrap"><input required minLength={6} maxLength={128} type={showConfirmPassword ? "text" : "password"} autoComplete="new-password" value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} /><VisibilityToggle visible={showConfirmPassword} onToggle={() => setShowConfirmPassword((visible) => !visible)} field="重复密码" /></div></label>}
          {error && <p className="error-message">{error}</p>}
          <button className="primary-button account-submit" type="submit" disabled={submitting}>{submitting ? "请稍候…" : mode === "login" ? "登录" : "注册并登录"}</button>
        </form>
      </>}
    </section>
  </main>;
}

function VisibilityToggle({ visible, onToggle, field }: { visible: boolean; onToggle: () => void; field: string }) {
  return <button className="visibility-toggle" type="button" aria-label={`${visible ? "隐藏" : "显示"}${field}`} title={`${visible ? "隐藏" : "显示"}${field}`} onClick={onToggle}>
    <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12Z" />
      <circle cx="12" cy="12" r="3" />
      {!visible && <path d="m4 4 16 16" />}
    </svg>
  </button>;
}
